import { Project } from "projen/lib";
import { AgentRuleBundle } from "../agent-config-options";
import { AGENT_RULE_SCOPE } from "../types";
import { hasDep } from "./utils";

/**
 * Projen bundle — auto-detected when `projen` is in dependencies.
 */
export const projenBundle: AgentRuleBundle = {
  name: "projen",
  description: "Projen conventions, synthesis workflow, .projenrc.ts patterns",
  appliesWhen: (project: Project) => hasDep(project, "projen"),
  rules: [
    {
      name: "development-commands",
      description:
        "Projen development commands for building, testing, linting, and validating changes",
      // ALWAYS scope: developers ask "how do I build/test/lint?" from
      // anywhere in the repo, not only when editing projen config.
      // Consumers that want to narrow the load can override via
      // `agentConfig.additionalRulePaths` or `excludeRules`.
      scope: AGENT_RULE_SCOPE.ALWAYS,
      content: [
        "# Development Commands",
        "",
        "This project uses Projen to manage configuration and Turborepo to orchestrate builds across the monorepo. Run all commands from the **repository root** unless otherwise noted.",
        "",
        "## Synthesizing Projen Configuration",
        "",
        "After modifying any file in `projenrc/` or `.projenrc.ts`, regenerate project files using the safe three-step sequence:",
        "",
        "```sh",
        "pnpm i",
        "pnpm exec projen",
        "pnpm i",
        "```",
        "",
        "All three steps are required:",
        "",
        "1. The leading `pnpm i` guarantees `node_modules` matches `pnpm-lock.yaml` before synth. Without this step, `pnpm exec projen` may synth against a stale version of any pnpm-resolved component (configulator itself, projen, or any plugin) that the lockfile has already moved past — most commonly right after `git pull` updates the lockfile or on a fresh checkout. The result is phantom drift in `.claude/`, `.github/labels.yml`, `CLAUDE.md`, and other configulator-generated files that looks like a real bundle change but vanishes after running `pnpm i`.",
        "2. `pnpm exec projen` regenerates files against the now-synced templates.",
        "3. The trailing `pnpm i` picks up any dependency changes projen wrote into `package.json` during the synth pass and refreshes the lockfile.",
        "",
        "## Building",
        "",
        "**Full monorepo build** (compile + test + package, all sub-packages via Turborepo):",
        "",
        "```sh",
        "pnpm build:all",
        "```",
        "",
        "**Root project only** (synthesize + compile + lint):",
        "",
        "```sh",
        "pnpm build",
        "```",
        "",
        "**Single sub-package** (compile only):",
        "",
        "```sh",
        "pnpm --filter @codedrifters/<package> compile",
        "```",
        "",
        "Replace `<package>` with `configulator`, `constructs`, or `utils`.",
        "",
        "## Testing",
        "",
        "**Run tests for a single sub-package:**",
        "",
        "```sh",
        "pnpm --filter @codedrifters/<package> test",
        "```",
        "",
        "This runs ESLint + Vitest for the specified package.",
        "",
        "**Run only Vitest (skip lint) in a sub-package:**",
        "",
        "```sh",
        "cd packages/@codedrifters/<package>",
        "pnpm exec vitest run",
        "```",
        "",
        "**Run a specific test file:**",
        "",
        "```sh",
        "cd packages/@codedrifters/<package>",
        "pnpm exec vitest run src/path/to/file.test.ts",
        "```",
        "",
        "## Linting",
        "",
        "**Lint the root project:**",
        "",
        "```sh",
        "pnpm eslint",
        "```",
        "",
        "**Lint a single sub-package:**",
        "",
        "```sh",
        "pnpm --filter @codedrifters/<package> eslint",
        "```",
        "",
        "## Resetting Build Artifacts",
        "",
        "**Reset everything** (all packages + root):",
        "",
        "```sh",
        "pnpm reset:all",
        "```",
        "",
        "**Reset root only:**",
        "",
        "```sh",
        "pnpm reset",
        "```",
        "",
        "## Validating Changes Are Complete",
        "",
        "After finishing implementation work, validate that changes are correct by running the appropriate commands depending on what was changed:",
        "",
        "1. **Projen config changes** (`projenrc/`, `.projenrc.ts`):",
        "   - Run the three-step regen sequence: `pnpm i`, then `pnpm exec projen`, then `pnpm i` again",
        "   - Verify no unexpected generated file changes with `git diff`",
        "",
        "2. **Source code changes** (in a sub-package):",
        "   - Compile: `pnpm --filter @codedrifters/<package> compile`",
        "   - Test: `pnpm --filter @codedrifters/<package> test`",
        "",
        "3. **Changes spanning multiple packages**:",
        "   - Run `pnpm build:all` to validate the full monorepo build",
        "",
        "4. **Root-level changes** (projenrc, root config):",
        "   - Run `pnpm build` to validate root synthesis + compilation + lint",
        "",
        "## Command Reference",
        "",
        "| Task | Command |",
        "|------|---------|",
        "| Regenerate projen configuration | `pnpm i && pnpm exec projen && pnpm i` |",
        "| Install deps | `pnpm i` |",
        "| Full monorepo build | `pnpm build:all` |",
        "| Root build only | `pnpm build` |",
        "| Compile one package | `pnpm --filter @codedrifters/<pkg> compile` |",
        "| Test one package | `pnpm --filter @codedrifters/<pkg> test` |",
        "| Lint one package | `pnpm --filter @codedrifters/<pkg> eslint` |",
        "| Lint root | `pnpm eslint` |",
        "| Reset all artifacts | `pnpm reset:all` |",
      ].join("\n"),
      tags: ["workflow"],
    },
    {
      name: "agent-rules-customization",
      description:
        "How to customize agent rules for this repo via the Projen project definition",
      scope: AGENT_RULE_SCOPE.FILE_PATTERN,
      filePatterns: [
        "projenrc/**/*.ts",
        ".projenrc.ts",
        ".claude/rules/*.md",
        ".cursor/rules/*.mdc",
        "CLAUDE.md",
      ],
      content: [
        "# Customizing Agent Rules",
        "",
        "Agent rules for Claude and Cursor are **generated** by configulator's `AgentConfig` component. The generated output files (`.claude/rules/`, `.cursor/rules/`, `CLAUDE.md`) must not be edited directly — they are overwritten on every `pnpm exec projen` run.",
        "",
        "## Adding Repo-Specific Rules",
        "",
        "Rules that only apply to this repository should be added to the `agentConfig.rules` array in the Projen project definition (`.projenrc.ts` or `projenrc/*.ts`):",
        "",
        "```typescript",
        "agentConfig: {",
        "  rules: [",
        "    {",
        "      name: 'my-repo-rule',",
        "      description: 'What this rule does',",
        "      scope: AGENT_RULE_SCOPE.ALWAYS, // or FILE_PATTERN with filePatterns",
        "      content: '# My Rule\\n\\n- Guideline 1\\n- Guideline 2',",
        "    },",
        "  ],",
        "}",
        "```",
        "",
        "## Extending Existing Bundle Rules",
        "",
        "To append repo-specific content to a rule provided by a configulator bundle (without replacing it), use `agentConfig.ruleExtensions`:",
        "",
        "```typescript",
        "agentConfig: {",
        "  ruleExtensions: {",
        "    'typescript-conventions': '## Additional Guidelines\\n\\n- My custom guideline',",
        "  },",
        "}",
        "```",
        "",
        "The same pattern applies to bundled sub-agents and skills. Use `agentConfig.subAgentExtensions` to append project-specific content to a sub-agent prompt and `agentConfig.skillExtensions` to append content to a skill's `SKILL.md` body. Both options mirror `ruleExtensions` exactly — keys are sub-agent / skill names, values are markdown appended after a horizontal rule, and unknown keys are silently ignored.",
        "",
        "```typescript",
        "agentConfig: {",
        "  subAgentExtensions: {",
        "    'requirements-writer': '## Project-Specific Gaps\\n\\n- My custom guidance',",
        "  },",
        "  skillExtensions: {",
        "    'write-requirement': '## Project-Specific Templates\\n\\n- My custom template note',",
        "  },",
        "}",
        "```",
        "",
        "## After Any Change",
        "",
        "Run the three-step regen sequence to regenerate the output files: `pnpm i`, then `pnpm exec projen`, then `pnpm i` again. The leading `pnpm i` syncs `node_modules` with the lockfile before synth so `pnpm exec projen` runs against the right configulator/projen/plugin versions; the trailing `pnpm i` refreshes the lockfile for any dependency changes projen made during synth.",
      ].join("\n"),
      tags: ["workflow"],
    },
    {
      name: "projen-conventions",
      description: "Projen configuration patterns and best practices",
      scope: AGENT_RULE_SCOPE.FILE_PATTERN,
      filePatterns: ["projenrc/**/*.ts", ".projenrc.ts"],
      content: [
        "# Projen Conventions",
        "",
        "## Configuration Management",
        "",
        "- **Never edit generated files** (`package.json`, `tsconfig.json`, etc. marked with `// ~~ Generated by projen`)",
        "- Edit Projen configuration in:",
        "  - `.projenrc.ts` (root)",
        "  - `projenrc/*.ts` (package-specific)",
        "- After making Projen changes, run the three-step regen sequence: `pnpm i`, `pnpm exec projen`, `pnpm i`. The leading `pnpm i` ensures `node_modules` matches the lockfile so synth runs against the right configulator/projen/plugin versions; without it, synth can produce phantom drift in generated files. The trailing `pnpm i` picks up dependency changes projen wrote during synth.",
        "",
        "## Workspace Dependencies",
        "",
        "When adding dependencies between packages in a monorepo:",
        "",
        '- Use the workspace protocol: `project.addDeps("@org/sibling-pkg@workspace:*")`',
        "- This ensures the local version is always resolved, not a registry version",
        '- For dev dependencies: `project.addDevDeps("@org/sibling-pkg@workspace:*")`',
        "",
        "## The `configureMyProject` Pattern",
        "",
        "Each package should expose a `configureMyProject(options)` factory function that creates and configures its Projen project:",
        "",
        "```typescript",
        "export function configureMyProject(",
        "  options: MyProjectOptions,",
        "): TypeScriptProject {",
        "  const project = new TypeScriptProject({",
        "    ...options,",
        "    // package-specific defaults",
        "  });",
        "",
        "  // Attach components, configure rules, etc.",
        "  return project;",
        "}",
        "```",
        "",
        "This pattern keeps `.projenrc.ts` clean and makes package configuration reusable and testable.",
        "",
        "## Custom Projen Components",
        "",
        "All custom components must extend `Component` from Projen and use a static `.of()` factory for discovery:",
        "",
        "```typescript",
        "export class MyComponent extends Component {",
        "  public static of(project: Project): MyComponent | undefined {",
        "    const isDefined = (c: Component): c is MyComponent =>",
        "      c instanceof MyComponent;",
        "    return project.components.find(isDefined);",
        "  }",
        "",
        "  constructor(project: Project, options: MyComponentOptions) {",
        "    super(project);",
        "    // Implementation",
        "  }",
        "}",
        "```",
        "",
        "## Component Authoring",
        "",
        "- Export project types and utilities from `index.ts`",
        "- Follow Projen's component lifecycle patterns",
        "- Use `merge` from `ts-deepmerge` for deep merging configuration objects",
        "- Components should be reusable and configurable; use TypeScript interfaces for component options",
        "- Provide sensible defaults while allowing customization",
        "- Document public APIs with minimal JSDoc; put extended documentation in markdown",
        "",
        "## Dependencies",
        "",
        "- Dependencies managed through Projen configuration, not directly in `package.json`",
        '- Use catalog dependencies when available (e.g., `"projen": "catalog:"`)',
        "- Peer dependencies for shared libraries (e.g., `constructs`, `projen`, `aws-cdk-lib`)",
        '- Use workspace protocol for internal packages: `"@org/pkg@workspace:*"`',
      ].join("\n"),
      tags: ["workflow"],
    },
  ],
};
